
import { useContext, useCallback } from "react";
import { SessionContext } from "@/core/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

/**
 * Generic hook for accessing and managing the current user session
 * Can be used in any application with Supabase authentication
 */
export const useSession = () => {
  const { session, loading, refreshSession } = useContext(SessionContext);
  const { toast } = useToast();

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error({
          message: "Sign out error",
          error: new Error(error.message),
        });
        
        toast({
          title: "Error signing out",
          description: error.message,
          variant: "destructive",
        });
        
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error({
        message: "Sign out exception",
        error: error instanceof Error ? error : new Error(String(error)),
      });
      
      toast({
        title: "Error signing out",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
      
      return false;
    }
  }, [toast]);

  return {
    session,
    user: session?.user || null,
    isLoading: loading,
    isAuthenticated: !!session?.user,
    hasSessionError: !!session?.error,
    refreshSession,
    signOut,
  };
};
